// If the watch were lost, that purge would never happen and stale saved Rx / Haney / designs would
// linger keyed to watch re-learns them even if this record were somehow thin.
import { create } from "zustand";

export interface MergeWatch {
  merge_id: string;
  /* * the name the user gave the combined field — what the strip says it is building */
  name: string;
  /* * the sources, for the purge on completion and for honest copy while it runs */
  source_field_ids: string[];
  source_names: string[];
  status: "authorized" | "merging" | "complete" | "failed";
  /*
   *
   * compute's words, not a user-facing message ("TypeError: string argument without an encoding").
   * Shown behind a details toggle rather than as the headline — but SHOWN, because a blank error is
   * indistinguishable from a hung request, which is what actually saw.
   */
  failure_reason?: string | null;
  /* * epoch ms — drives "this is taking a while" copy without a second clock */
  started_at: number;
}

const KEY = "sm_merge_watch";

function load(): Record<string, MergeWatch> {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return {};
    return parsed as Record<string, MergeWatch>;
  } catch {
    // A corrupt entry must not take the dashboard down with it.
    return {};
  }
}

function save(byId: Record<string, MergeWatch>) {
  try {
    localStorage.setItem(KEY, JSON.stringify(byId));
  } catch {
    // Storage full or blocked — the watch degrades to this session, which is still better than
    // throwing inside a state update.
  }
}

interface MergeWatchState {
  byId: Record<string, MergeWatch>;
  /* * start watching a merge the moment the authorise returns */
  start: (
    w: Omit<MergeWatch, "status" | "started_at"> & { started_at?: number },
  ) => void;
  /* * record what the status endpoint said */
  update: (mergeId: string, patch: Partial<MergeWatch>) => void;
  /* * the user dismissed a finished/failed one, or it completed and was cleaned up */
  dismiss: (mergeId: string) => void;
  reset: () => void;
}

export const useMergeWatch = create<MergeWatchState>((set) => ({
  byId: load(),
  start: (w) =>
    set((s) => {
      const next = {
        ...s.byId,
        [w.merge_id]: {
          ...w,
          status: "authorized" as const,
          started_at: w.started_at ?? Date.now(),
        },
      };
      save(next);
      return { byId: next };
    }),
  update: (mergeId, patch) =>
    set((s) => {
      const cur = s.byId[mergeId];
      if (!cur) return s;
      const next = { ...s.byId, [mergeId]: { ...cur, ...patch } };
      save(next);
      return { byId: next };
    }),
  dismiss: (mergeId) =>
    set((s) => {
      const next = { ...s.byId };
      delete next[mergeId];
      save(next);
      return { byId: next };
    }),
  reset: () => {
    save({});
    return set({ byId: {} });
  },
}));

/* * Still running — the only ones worth polling. */
export const isWatching = (w: MergeWatch): boolean =>
  w.status === "authorized" || w.status === "merging";
